import React, { useRef, useState } from "react";
import Header from "../components/Header";
import { ProviderSelect } from "../components/ProviderSelect";
import { useAuth } from "../context/AuthContext";
import { Camera, CalendarClock, Globe, PenTool, Scissors, PersonStanding, Send, Check, Upload } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FORMS = [
  {
    type: "photography", providerType: "photography", label: "Book photography", icon: Camera,
    fields: [
      { key: "date", label: "Date*", type: "date", required: true },
      { key: "time", label: "Time*", type: "time", required: true },
      { key: "location", label: "Location*", required: true, placeholder: "City / studio / on-site" },
      { key: "concept", label: "What are we shooting?*", textarea: true, required: true, placeholder: "Portraits, event, product, editorial…" },
    ],
  },
  {
    type: "svcconsult", providerType: "consulting", label: "Book consulting", icon: CalendarClock,
    fields: [
      { key: "topic", label: "What do you need help with?*", textarea: true, required: true },
      { key: "preferred_date", label: "Preferred date", type: "date" },
      { key: "preferred_time", label: "Preferred time", type: "time" },
    ],
  },
  {
    type: "website", providerType: "website", label: "Website design", icon: Globe,
    fields: [
      { key: "project", label: "Project / business name*", required: true },
      { key: "current_site", label: "Current site (if any)", placeholder: "https://…" },
      { key: "scope", label: "What do you need?*", textarea: true, required: true, placeholder: "New site, redesign, online store, landing page…" },
      { key: "budget", label: "Budget range", placeholder: "e.g. $500–$1,500" },
    ],
  },
  {
    type: "graphic", providerType: "graphic", label: "Graphic design", icon: PenTool,
    fields: [
      { key: "project_type", label: "Project type*", select: ["logo", "cover art", "poster / flyer", "merch design", "branding", "other"], required: true },
      { key: "description", label: "Describe the project*", textarea: true, required: true, placeholder: "Vision, references, sizes needed…" },
      { key: "deadline", label: "Deadline", type: "date" },
    ],
  },
  {
    type: "fashiondesign", providerType: "fashiondesign", label: "Fashion design", icon: Scissors,
    fields: [
      { key: "garment", label: "Garment / piece*", required: true, placeholder: "Jacket, dress, full look…" },
      { key: "quantity", label: "Quantity", type: "number", min: 1, placeholder: "e.g. 1" },
      { key: "description", label: "Describe the vision*", textarea: true, required: true, placeholder: "Style, materials, references…" },
      { key: "deadline", label: "Deadline", type: "date" },
    ],
  },
  {
    type: "modelcasting", providerType: "modelcasting", label: "Model casting", icon: PersonStanding,
    upload: { key: "portfolio_url", label: "Model portfolio upload" },
    fields: [
      { key: "availability", label: "Availability*", required: true, placeholder: "e.g. weekends, June 20–30…" },
      { key: "top_size", label: "Top size*", required: true, placeholder: "e.g. M" },
      { key: "bottom_size", label: "Bottom size*", required: true, placeholder: "e.g. 32" },
      { key: "shoe_size", label: "Shoe size*", required: true, placeholder: "e.g. 10.5" },
      { key: "experience", label: "Experience / past work", textarea: true, placeholder: "Shows, shoots, campaigns…" },
    ],
  },
];

const blankForm = (f) => {
  const base = { name: "", phone: "", instagram: "", email: "", provider_id: "" };
  f.fields.forEach((fl) => { base[fl.key] = fl.select ? fl.select[0] : ""; });
  return base;
};

const ServiceForm = ({ cfg }) => {
  const { token } = useAuth();
  const fileRef = useRef(null);
  const [form, setForm] = useState(() => blankForm(cfg));
  const [state, setState] = useState("idle");
  const [uploadUrl, setUploadUrl] = useState("");
  const [uploading, setUploading] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const uploadFile = async (file) => {
    if (!file || !token) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await fetch(`${API}/api/uploads`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        credentials: "include",
        body: fd,
      });
      const data = await res.json();
      if (res.ok) setUploadUrl(`${API}${data.url}`);
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setState("busy");
    try {
      const body = { type: cfg.type, ...form };
      if (cfg.upload) body[cfg.upload.key] = uploadUrl;
      const res = await fetch(`${API}/api/services/requests`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      setState(res.ok ? "ok" : "error");
    } catch {
      setState("error");
    }
  };

  return (
    <form className="req-form" onSubmit={submit} data-testid={`service-form-${cfg.type}`}>
      <h3 className="req-title">{cfg.label}</h3>
      <div className="req-grid">
        <label className="req-field"><span className="req-label">Full name*</span>
          <input className="req-input" value={form.name} onChange={set("name")} required data-testid={`svc-${cfg.type}-name`} /></label>
        <label className="req-field"><span className="req-label">Number*</span>
          <input className="req-input" type="tel" value={form.phone} onChange={set("phone")} required data-testid={`svc-${cfg.type}-phone`} /></label>
        <label className="req-field"><span className="req-label">Instagram handle</span>
          <input className="req-input" value={form.instagram} onChange={set("instagram")} placeholder="@yourhandle" data-testid={`svc-${cfg.type}-instagram`} /></label>
        <label className="req-field"><span className="req-label">Email*</span>
          <input className="req-input" type="email" value={form.email} onChange={set("email")} required data-testid={`svc-${cfg.type}-email`} /></label>
        {cfg.fields.filter((f) => !f.textarea && !f.select).map((f) => (
          <label className="req-field" key={f.key}><span className="req-label">{f.label}</span>
            <input className="req-input" type={f.type || "text"} min={f.min} value={form[f.key]}
              onChange={set(f.key)} required={!!f.required} placeholder={f.placeholder}
              data-testid={`svc-${cfg.type}-${f.key}`} /></label>
        ))}
        {cfg.fields.filter((f) => f.select).map((f) => (
          <label className="req-field" key={f.key}><span className="req-label">{f.label}</span>
            <select className="req-input" value={form[f.key]} onChange={set(f.key)} data-testid={`svc-${cfg.type}-${f.key}`}>
              {f.select.map((o) => <option key={o} value={o}>{o}</option>)}
            </select></label>
        ))}
      </div>
      {cfg.fields.filter((f) => f.textarea).map((f) => (
        <label className="req-field" key={f.key}><span className="req-label">{f.label}</span>
          <textarea className="req-input" rows={2} value={form[f.key]} onChange={set(f.key)}
            required={!!f.required} placeholder={f.placeholder} data-testid={`svc-${cfg.type}-${f.key}`} /></label>
      ))}
      {cfg.upload && (
        <div className="req-field">
          <span className="req-label">{cfg.upload.label}</span>
          {token ? (
            <div className="gd-upload-row">
              <button type="button" className="btn-ghost" onClick={() => fileRef.current?.click()}
                disabled={uploading} data-testid={`svc-${cfg.type}-upload-btn`}>
                <Upload size={15} /> {uploading ? "Uploading…" : uploadUrl ? "Replace file" : "Choose file"}
              </button>
              {uploadUrl && <span className="gd-upload-ok"><Check size={14} /> attached</span>}
              <input ref={fileRef} type="file" hidden accept="image/*,video/*,.pdf"
                onChange={(e) => uploadFile(e.target.files?.[0])} />
            </div>
          ) : (
            <p className="gd-upload-note">Log in to attach your portfolio — or drop a link in the experience box.</p>
          )}
        </div>
      )}
      <ProviderSelect type={cfg.providerType} value={form.provider_id}
        onChange={(v) => setForm({ ...form, provider_id: v })} />
      {state === "ok" ? (
        <div className="shirt-claim-ok" data-testid={`svc-${cfg.type}-ok`}><Check size={16} /> Request sent — we'll email you.</div>
      ) : (
        <button className="btn-solid" type="submit" disabled={state === "busy"} data-testid={`svc-${cfg.type}-submit`}>
          <Send size={15} /> {state === "busy" ? "Sending…" : "Send request"}
        </button>
      )}
      {state === "error" && <div className="cart-error">Couldn't send — check the fields and try again.</div>}
    </form>
  );
};

const Services = () => {
  const [open, setOpen] = useState("");
  React.useEffect(() => { document.title = "TMM|SERVICES"; }, []);

  return (
    <>
      <Header hoverColor="rgba(120,200,190,1)" compact={true} />
      <div className="services-page" data-testid="services-page">
        <header className="services-head">
          <img src="/img/nav_services_hero.png" alt="Services" className="services-head-mark" data-testid="services-head-mark" />
          <p className="fashion-eyebrow">Book the team — or book a member directly.</p>
        </header>
        <div className="req-toggle-row services-toggles">
          {FORMS.map((f) => (
            <button key={f.type} className="btn-ghost"
              onClick={() => setOpen(open === f.type ? "" : f.type)}
              data-testid={`service-toggle-${f.type}`}>
              <f.icon size={16} /> {f.label}
            </button>
          ))}
        </div>
        {FORMS.filter((f) => f.type === open).map((f) => <ServiceForm key={f.type} cfg={f} />)}
      </div>
    </>
  );
};

export default Services;
